import utils from '@/services/utils';
import { mapActions } from 'vuex';
import { Loader } from '@wiwdata/zoomies';
import constants from '@/services/constants';
import http from '@/services/http';
import ActionBar from '@/components/ActionBar.vue';
import FormEntry from '@/components/FormEntry.vue';
import MessageBox from '@/components/MessageBox.vue';
import RadioInput from '@/components/RadioInput.vue';
import DateRangeInput from '@/components/DateRangeInput.vue';
import moment from 'moment';
import VerticalRadioInput from './VerticalRadioInput.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import VerticalCheckBoxInput from '../../components/VerticalCheckBoxInput.vue';

/**
 * Calculate the date on which the finance department will recognize the
 * revenue given the date on which the service is started. This is for the sake
 * of financial statements.
 *
 * Revenue is recognized as starting on the first day of the month in which the
 * service starts.
 */
function revRecStart() {
  const target = this.billingStartDate;
  if (target === null) {
    return target;
  }
  return utils.startOfMonth(target);
}

/**
 * Calculate the last date on which the finance department will recognize the
 * revenue given the ending date of the service. This is for the sake of
 * financal statements.
 *
 * Revenue is recognized as ending on the last day of the month previous to the
 * month in which the service actually ends. As an example, if service ends on
 * September 20th, then the revenue from that service will stop being
 * recognized on the last day of August. The exception to this rule is when
 * the service starts and ends in the same month, in which case the end is the
 * last day of the month in which service is provided.
 */
function revRecEnd() {
  if (!this.billingEndDate || !this.billingStartDate) {
    return null;
  }

  const end = moment(this.billingEndDate);
  const start = moment(this.billingStartDate);

  if (start.isSame(end, 'month')) {
    return utils.endOfMonth(start);
  }

  return utils.endOfMonth(moment(end).subtract(1, 'month'));
}

function planDefinedUnits() {
  return this.plans.filter((plan) => plan.plan_id === this.planId)[0].employees;
}

function confirmTransaction(value) {
  if (value === 'yes') {
    this.save();
  }
  this.confirming = false;
}

function filteredPlans() {
  return this.plans.filter((plan) => plan.is_tiered === this.isTiered);
}

/**
 * It seems difficult to set this directly when we need to based on a disabled RadioInput
 * component, so we use a computed function that can help us figure out what to do.
*/
function action() {
  return this.canChooseAction ? this.selectedAction : 'both';
}

function canChooseAction() {
  // If it is a:
  // - monthly transaction (0)
  // - upgrade transaction (6)
  // - annual transaction (7)
  // then we want the action boxes enabled.
  const allowed = ['0', '6', '7'];
  return allowed.includes(this.transactionType);
}

function canBookTransaction() {
  // Given the user canChooseAction, determine if they are allowed to book a
  // transaction. If they are using a card, the transaction clearly is not
  // being just booked.
  return this.canChooseAction && !this.useCard;
}

function canBookAndReceive() {
  // We generally only want to allow this if we are using a card, as long as the amount is > 0
  return !this.hasNonZeroAmount || this.useCard;
}

function hasNonZeroAmount() {
  // NaN is not greater than zero, so this should always be safe
  return this.amount > 0;
}

function actionOptions() {
  return [
    { label: 'Booking transaction', value: 'book', disabled: !this.canBookTransaction },
    {
      label: 'Receiving funds for previously booked transaction',
      value: 'receive',
    },
    { label: 'Booking and receiving funds for transaction', value: 'both', disabled: !this.canBookAndReceive },
  ];
}

function confirmConditions() {
  const conditions = [];
  const amount = (this.amount || 0) + (this.tax || 0);
  if (this.action === 'receive' && !this.useCard) {
    conditions.push('Receive transaction WITHOUT charging card');
  }
  if (amount === 0) {
    conditions.push('Create zero dollar transaction');
  }
  if (!this.isMaster) {
    conditions.push('Create a transaction on a CHILD account');
  }
  return conditions;
}

function getValidationErrors() {
  if (this.action === 'book' && this.useCard) {
    return (
      'The "Use Card" and "Booking transaction" options conflict. Please select '
      + ' an action that includes receiving funds.'
    );
  }

  if (this.action === 'both' && !this.useCard && this.hasNonZeroAmount) {
    return 'Booking and receiving in one transaction currently requires a credit card.';
  }

  // No errors, return a falsey value
  return '';
}

function save() {
  const startDate = utils.toIsoString(this.billingStartDate);
  const endDate = utils.toIsoString(this.billingEndDate);
  const isBooked = (this.action === 'book') || (this.action === 'both');
  const isReceived = (this.action === 'receive') || (this.action === 'both');
  const planUnits = this.isTiered ? Number(this.planUnits) : 0;
  const result = {
    account_id: this.$route.params.accountId,
    amount: this.amount || 0,
    billing_start_date: startDate,
    billing_end_date: endDate,
    booked: this.canChooseAction ? isBooked : true,
    received: this.canChooseAction ? isReceived : true,
    discount: (parseFloat(this.discountPercent) / 100) || 0,
    internal_notes: this.internalNotes,
    plan_id: this.planId,
    plan_units: planUnits,
    tax: this.tax || 0,
    transaction_type: this.transactionType,
    use_card: this.useCard,
    user_summary: this.userSummary,
    has_scheduling: this.selectedProducts.includes('Scheduling'),
    has_attendance: this.selectedProducts.includes('Attendance'),
  };
  const errors = this.getValidationErrors();
  if (errors) {
    this.errorMsg = errors;
    return;
  }
  http.addTransaction(result).then((response) => {
    if (response.data.status_code === 200) {
      this.$router.push({ path: `/accounts/${this.$route.params.accountId}` });
    } else {
      this.errorMsg = response.data.status_message || 'Server Error';
    }
  });
}

function mounted() {
  const { accountId } = this.$route.params;
  const plansPromise = http.getPlans().then((response) => {
    if (response.data.status_code === 200) {
      this.plans = response.payload.data;
    } else {
      const errMsg = 'Unable to load account plan types.';
      this.$router.push({ path: '/', query: { msg: errMsg } });
    }
  });
  const detailsPromise = this.getAccountDetails(accountId).then((detailResponse) => {
    const account = detailResponse;
    this.companyName = account.company;
    this.isMaster = (account.master_id === account.account_id || account.master_id === 0);
    this.isTiered = account.plan_is_tiered === true;
    this.planId = account.plan_id;
    this.planUnits = account.plan_units;
    this.loading = false;
    this.transactionType = account.billing_type === 0 ? '0' : '7';
    this.productOptions = [
      { label: 'Scheduling', value: 'Scheduling', isChecked: account.has_scheduling },
      { label: 'Attendance', value: 'Attendance', isChecked: account.has_attendance },
    ];
    this.selectedProducts = this.productOptions
      .reduce((products, option) => {
        if (option.isChecked) {
          products.push(option.value);
        }
        return products;
      }, []);

    return null;
  })
    .catch((error) => this.$router.push({ path: '/', query: { msg: error.message } }));
  return Promise.all([plansPromise, detailsPromise]);
}

function transactionTypes() {
  const userAllowedTypes = ['0', '6', '7', '8', '13', '14'];
  return constants.TRANSACTION_TYPES.filter((t) => userAllowedTypes.includes(t.id));
}

/**
 * Updates selectedProducts with the event values
 * @param e The event containing the products selected
 */
function updateSelectedProducts(e) {
  this.selectedProducts = e.checkedValues;
}

function data() {
  return {
    cardOptions: [{ label: 'Yes', value: true }, { label: 'No', value: false }],
    amount: null,
    billingStartDate: null,
    billingEndDate: null,
    confirming: false,
    companyName: '',
    description: '',
    discountPercent: '',
    errorMsg: null,
    internalNotes: '',
    isMaster: true,
    isTiered: true,
    loading: true,
    planId: '1',
    planType: '0',
    planUnits: 0,
    plans: [],
    tax: null,
    transactionType: '0',
    useCard: false,
    userSummary: '',
    productOptions: {},
    selectedProducts: [],
    selectedAction: 'book',
  };
}

export default {
  name: 'AddTransactionView',
  components: {
    ActionBar,
    ConfirmDialog,
    FormEntry,
    Loader,
    RadioInput,
    MessageBox,
    DateRangeInput,
    VerticalRadioInput,
    VerticalCheckBoxInput,
  },
  data,
  computed: {
    action,
    actionOptions,
    canBookTransaction,
    canBookAndReceive,
    canChooseAction,
    confirmConditions,
    filteredPlans,
    hasNonZeroAmount,
    planDefinedUnits,
    revRecStart,
    revRecEnd,
    transactionTypes,
  },
  mounted,
  methods: {
    confirmTransaction,
    getValidationErrors,
    save,
    updateSelectedProducts,
    ...mapActions('account', ['getAccountDetails']),
  },
};
